import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import cn from 'classnames';

import { Button, DynamicIconWrapper, Icons, SpinnerOverlay, Tooltip } from '@components/common';
import Table from '@components/common/Table';
import TextAvatar from '@components/common/TextAvatar/TextAvatar';

import { useQueryAssetById } from '@hooks/assets/useQueryAssetById';
import { useQueryAssetHistory } from '@hooks/assets/useQueryAssetHistory';
import { useQueryAssetHistoryUsersOptions } from '@hooks/assets/useQueryAssetHistoryUsersOptions';
import { useQueryAssetTypes } from '@hooks/assets/useQueryAssetTypes';

import { assetHistory as assetHistorySelector, auth } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	setOrganizationUser,
	setPage,
	setPageSize,
	setSearchValue,
	setSort,
	setUpdatedAt,
} from '@slices/assetHistory';
import { viewOnMap } from '@slices/map';

import { setFilterUtil } from '@utils/filterUtil';
import { checkGroupPanelAccess } from '@utils/permissions';

import { TAssetProfile } from '@interfaces/assets/asset';
import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	ISortBy,
	ITableDatepickerFilter,
	ITablePagination,
	ITableSearch,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';

import { mapGroupAccess } from '@constants/menuGroupAccess';
import { routes } from '@constants/routes';
import {
	searchPlaceholderText,
	searchTooltipText,
} from './constants';
import { viewOnMapTooltip } from '@components/routes/AdminPanel/AssetProfile/constants';

import { getColumns } from './columns';

import styles from './AssetHistory.module.scss';

const { map } = routes;

const AssetHistory = () => {
	const {
		accessPermission,
	} = useAppSelector(auth);

	const {
		assetOwnerOrganizationAdministrator,
		assetOwnerAttributeUpdater,
	} = accessPermission;

	const isMapModuleAvailable = checkGroupPanelAccess(accessPermission, mapGroupAccess);

	const { id } = useParams();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const {
		organizationUser,
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
		updatedAt,
	} = useAppSelector(assetHistorySelector);

	const { assetById: profile } = useQueryAssetById(id, true);

	const [selectedUser, setSelectedUser] = useState<TSelectOption<string> | null>(null);

	const isAssetOwner = assetOwnerOrganizationAdministrator || assetOwnerAttributeUpdater;

	const {
		assetHistoryByFilter,
		countOfRecords,
		isLoading,
	} = useQueryAssetHistory({
		assetId: id,
		isAssetOwner,
		organizationUser,
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		updatedAt,
		searchValue,
	});

	const { usersOptions } = useQueryAssetHistoryUsersOptions(id);

	const handleSort = (sortBy: Array<ISortBy>) => {
		dispatch(setSort({
			sortDirection: !!sortBy[0]?.desc,
			sortFieldName: sortBy[0]?.id,
		}));
	};

	const onSearchHandler = (searchValue: string) => dispatch(setSearchValue({ searchValue }));

	const pageHandler = (page: number) => dispatch(setPage({ page }));

	const pageSizeHandler = (pageSize: number) => dispatch(setPageSize({ pageSize }));

	const pagination: ITablePagination = {
		countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	};

	const handleViewOnMap = () => {
		if (!profile?.showOnMap) return;

		const marker: TAssetProfile = profile;

		void dispatch(viewOnMap({ marker }));
		navigate(map.index, { state: { isRedirectedFromAdminPanel: true } });
	};

	const sort: ITableSort = {
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	};

	const search: Array<ITableSearch> = [
		{
			initialValue: searchValue,
			name: 'searchValue',
			handler: onSearchHandler,
			isNumber: true,
			placeholder: searchPlaceholderText,
			tooltipText: searchTooltipText,
		},
	];

	const columns = getColumns({ navigate });

	const updatedDate = updatedAt?.from ? new Date(updatedAt.from) : null;

	const onChangeUpdatedDate = (value: number | null) => dispatch(setUpdatedAt({ updatedAt: value }));

	const dateFilters: Array<ITableDatepickerFilter> = [
		{
			name: 'updated',
			label: 'Update date',
			handleChange: onChangeUpdatedDate,
			selected: updatedDate,
		},
	];

	const filters: Array<TTableFilter> = [
		{
			name: 'userName',
			placeholder: 'Changed by',
			option: selectedUser,
			options: usersOptions,
			minWidth: '200px',
			setState: setSelectedUser,
			defaultValue: organizationUser,
		},
	];

	const onBackClickHandler = () => navigate(-1);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedUser,
			currentValue: organizationUser,
			setAction: setOrganizationUser,
			fieldName: 'organizationUser',
			dispatch,
		});
	}, [selectedUser, dispatch, organizationUser]);

	const { assetTypesSubtypes, isLoadingAssetTypes } = useQueryAssetTypes({ isAssetTypesSubtypesWithIconUrl: false });

	const assetTypeId = assetTypesSubtypes.find(({ value }) => profile?.assetType.id === value)?.type?.icon?.id;

	return (
		<div className={styles.assetHistoryProfileContainer}>
			{isLoadingAssetTypes && <SpinnerOverlay />}
			<Button
				className={styles.btnBackIncreasedHeight}
				icon={Icons.arrowLeft()}
				onClick={onBackClickHandler}
				text='Back'
				withoutBackground
			/>
			<div className={cn(styles.whiteWrapper, styles.nameSection)}>
				<div className={styles.icon}>
					<TextAvatar
						avatarId={assetTypeId}
						fontSize='20px'
						isProfileLogo
						isUnknown={!assetTypeId}
						size='75px'
					/>
				</div>
				<div>
					<div className={styles.name}>{profile?.assetId}</div>
					<div className={styles.type}>{profile?.assetType.assetTypeName}</div>
					{profile?.assetSubType?.assetSubTypeName
						&& <div className={styles.subtype}>{profile.assetSubType.assetSubTypeName}</div>
					}
				</div>
				{isMapModuleAvailable && (
					<div className={styles.location}>
						<Tooltip content={viewOnMapTooltip}>
							<div onClick={handleViewOnMap}>
								<DynamicIconWrapper
									icon={Icons.location}
									isDisabled={!profile?.showOnMap}
								/>
							</div>
						</Tooltip>
					</div>
				)}
			</div>
			<div className={styles.assetHistoryTableContainer}>
				<Table
					columns={columns}
					data={assetHistoryByFilter}
					dateFilters={dateFilters}
					filters={filters}
					isLoading={isLoading}
					isWithoutCheckbox
					isWithoutDeleteAction
					pagination={pagination}
					search={search}
					searchThreshold={0}
					sort={sort}
				/>
			</div>
		</div>
	);
};

export default AssetHistory;
